import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import logo from '../../assets/powered_by_xantage.png';
import sparkLogo from '../../assets/RLC white transparent background arrow only.png';
// import sparkLogo from '../../assets/RLCI Logo Only Reversed.jpg'; // SMALL LOGO
// import sparkLogo from '../../assets/RLCI Horizontal Logo Reversed.png'; // BIG LOGO
// import sparkLogoHorizontal from '../../assets/RLCI Horizontal Logo Reversed.png';
// import sparkLogoCircular from '../../assets/RLC white transparent background arrow only.png';
import { MdSmartToy, MdLightbulb, MdQuestionAnswer, MdDescription, MdArchive, MdPeople, MdFeedback, MdBusiness, MdLibraryBooks, MdMenu } from 'react-icons/md';
import { getAccessibleNavigationItems, NAV_SECTIONS } from '../../utils/rolePermissions';


const Sidebar = ({ onToggle }) => {
  // Initialize collapsed state from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  const { currentUser } = useContext(AuthContext);
  const location = useLocation();

  // Get accessible navigation items based on user role
  const accessibleNavItems = currentUser ? getAccessibleNavigationItems(currentUser.role) : [];

  // Icon mapping for dynamic icon rendering
  const iconMap = {
    'MdSmartToy': MdSmartToy,
    'MdLightbulb': MdLightbulb,
    'MdQuestionAnswer': MdQuestionAnswer,
    'MdDescription': MdDescription,
    'MdArchive': MdArchive,
    'MdPeople': MdPeople,
    'MdFeedback': MdFeedback,
    'MdBusiness': MdBusiness,
    'MdLibraryBooks': MdLibraryBooks
  };

  // Persist collapsed state to localStorage
  useEffect(() => {
    // Store as string to ensure consistent retrieval (localStorage only stores strings)
    localStorage.setItem('sidebarCollapsed', String(isCollapsed));
    // Notify parent component if callback provided
    if (onToggle) {
      onToggle(isCollapsed);
    }
  }, [isCollapsed, onToggle]);

  // Toggle sidebar function
  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Function to check if a path is active
  const isActivePath = (path) => {
    return location.pathname.includes(path);
  };

  // Render navigation icon dynamically
  const renderIcon = (iconName, size = 24) => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent size={size} /> : null;
  };

  return (
    <nav
      id="navigation"
      role="navigation"
      aria-label="Main navigation"
      className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}
    >
      {/* TOP SECTION */}
      <div className="sidebar-section sidebar-top">
        {/* Updated branded header with Spark logo */}
        <div className="sidebar-brand">
          {!isCollapsed ? (
            <div className="brand-text">
              <button
                className="sidebar-toggle"
                onClick={toggleSidebar}
                aria-label="Collapse Sidebar"
                title="Collapse Sidebar"
              >
                <MdMenu size={24} />
              </button>
              <div className="brand-logo">
                <img src={sparkLogo} alt="Spark logo" className="spark-logo" />
              </div>
              <div className="brand-name">
                <span>Rapid Learning Cycles</span>
                <span>Spark</span>
              </div>
            </div>
          ) : (
            <div
              className="brand-icon"
              onClick={toggleSidebar}
              style={{ cursor: 'pointer' }}
              title="Expand Sidebar"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault(); // Prevent default space scroll
                  toggleSidebar();
                }
              }}
            >
              <img src={sparkLogo} alt="Spark logo" className="spark-logo" />
            </div>
          )}
        </div>

        {/* Dynamic Navigation Menu based on user role.
            Items are grouped into sections (mock-up parity): a section label
            and/or divider is inserted whenever the group changes. */}
        <ul className="nav-menu">
          {accessibleNavItems.map((navItem, idx) => {
            // Map paths to tour data attributes
            const tourDataMap = {
              '/ai-coach': 'ai-coach',
              '/core-hypothesis': 'core-hypothesis',
              '/question-evaluator': 'question-evaluator',
              '/report-writer': 'report-writer',
              '/archive': 'archive'
            };
            const tourAttr = tourDataMap[navItem.path];

            const prevGroup = idx > 0 ? accessibleNavItems[idx - 1].group : null;
            const isNewGroup = navItem.group && navItem.group !== prevGroup;
            const sectionLabel = isNewGroup ? NAV_SECTIONS[navItem.group] : null;

            return (
              <React.Fragment key={navItem.path}>
                {isNewGroup && idx > 0 && <li className="nav-divider" aria-hidden="true" />}
                {isNewGroup && !isCollapsed && sectionLabel && (
                  <li className="nav-section-label">{sectionLabel}</li>
                )}
                <li
                  className={isActivePath(navItem.path) ? 'active' : ''}
                  data-tour={tourAttr}
                >
                  <Link to={navItem.path}>
                    <span className="nav-icon">
                      {renderIcon(navItem.icon, 24)}
                    </span>
                    {!isCollapsed && <span className="nav-text">{navItem.label}</span>}
                  </Link>
                </li>
              </React.Fragment>
            );
          })}
        </ul>

        {/* Debug info (only show in development) */}
        {process.env.NODE_ENV === 'development' && currentUser && !isCollapsed && (
          <div style={{ 
            fontSize: '0.7rem', 
            color: 'var(--rlc-border-strong)', 
            padding: '10px 15px',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            marginTop: '10px'
          }}>
            Role: {currentUser.role}<br/>
            Items: {accessibleNavItems.length}
          </div>
        )}
      </div>

      {/* MIDDLE SECTION - Model Selector (commented out as in original) */}
      {/* <div className="sidebar-section sidebar-middle">
        {!isCollapsed && (
          <div className="model-selector">
            <label htmlFor="model-select">LLM Model:</label>
            <select
              id="model-select"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="model-dropdown"
            >
              {models.map(model => (
                <option key={model.id} value={model.id}>
                  {model.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div> */}
      
      {/* BOTTOM SECTION */}
      <div className="sidebar-section sidebar-bottom">
        {/* Xantage logo at bottom - only show when expanded */}
        {!isCollapsed && (
          <div className="sidebar-logo">
            <img src={logo} alt="Xantage logo" />
          </div>
        )}
      </div>
    </nav>
  );
};

export default Sidebar;